import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Input,
  PrimaryButton,
  Card,
  SecondaryButton,
  INPUT_SIZES,
  RADIO_LABEL_POSITIONS,
  RADIO_SIZES,
  Radio,
  Loader,
  Dialog,
  BUTTON_VARIANTS,
  DropdownMenu,
  withTooltip,
} from "cdk-radial";
import config from "../config/app.conf.json";

import "./style.css";
import { PRICING_LIST_PLANS, VIEW_BUNDLES } from "./screen";
import withNavigationToBundles from "./withNavigationToBundles";
import PricingBreadcrumbs from "./PricingBreadcrumbs";
import AdvancePlanCard from "./AdvancePlanCard"
import {
  BUNDLE_STATUS,
  PLAN_STATUS,
  PLAN_VISIBILITY,
  BUNDLE_PLAN_OPTIONS,
  ADVANCE_PLAN_CHANGE,
  RETIREMENT_TYPE,
  RETIREMENT_MESSAGES,
} from "../Utils/constants";
import {
  determineRetirementType,
  transformContainerForAPI,
  transformTierForAPI,
} from "./utils/pricingPlanHelpers";

function PricingAddPlan(props) {
  const {
    auth,
    setScreen,
    resetBundlePricingPlans,
    resetSelectedPricingPlan,
    resetBundlePlan,
    createBundlePricePlan,
    updateBundlePricePlan,
    fetchPlanDetails,
    clearFetchPlanDetailsError,
    loadSubPlansFromDetails,
    setBundlePlanDetails,
    clearAddEditPricePlanError,
    ApiBundleManagementReducer,
  } = props;
  const [showPublicPlanDialog, setShowPublicPlanDialog] = useState(false);
  const [showUpdateConfirmDialog, setShowUpdateConfirmDialog] = useState(false);
  const [retirementType, setRetirementType] = useState(null);
  const [isDealerPortalChecked, setIsDealerPortalChecked] = useState(true);

  const [isPlanNameHasError, setIsPlanNameHasError] = useState("");
  const [isInstallFeeValid, setIsInstallFeeValid] = useState(true);
  const [isInstallFeeCatalogNumberValid, setIsInstallFeeCatalogNumberValid] = useState(true);
  const [isMonthCostValid, setIsMonthCostValid] = useState(true);
  const [isCatalogNumberValid, setIsCatalogNumberValid] = useState(true);
  const [isOverageCatalogNumberValid, setIsOverageCatalogNumberValid] = useState(true);

  const [originalPrice, setOriginalPrice] = useState("");
  const [originalInstallFeeEnabled, setOriginalInstallFeeEnabled] = useState(false);
  const [originalInstallFeeAmount, setOriginalInstallFeeAmount] = useState("");
  const {
    bundlePricingPlans,
    selectedPricingPlan,
    addOrEditPricePlanError,
    showAddEditPriceErrorDialog,
    creatingOrEditingPlan,
    bundlePlan,
    selectedBundle,
    fetchingPlanDetails,
    planDetails,
    errorFetchingPlanDetails,
    errorFetchingPlanDetailsMessage,
  } = ApiBundleManagementReducer;

  const {
    planName,
    visibility: planVisibility,
    isInstallFeeEnabled,
    installFee,
    installFeeCatalogNumber,
    planType,
    monthCost,
    planCatalogNumber: catalogNumber,
    overageCatalogNumber,
    basic,
    plus,
    pro,
    custom,
  } = bundlePlan;

  const { accessToken } = auth;

  const isEditMode = !!(selectedPricingPlan && selectedPricingPlan.id);

  // Fetch plan details when entering edit mode
  useEffect(() => {
    if (isEditMode && selectedBundle?.id && selectedPricingPlan?.id) {
      const isMatchingPlan = planDetails && (
        planDetails.id === selectedPricingPlan.id ||
        (planDetails.groupId && planDetails.groupId === selectedPricingPlan.groupId)
      );

      if (isMatchingPlan) {
        return;
      }

      if (fetchingPlanDetails) {
        return;
      }

      fetchPlanDetails({
        accessToken,
        bundleId: selectedBundle.id,
        pricePlanId: selectedPricingPlan.id,
        groupId: selectedPricingPlan.groupId,
        planType: selectedPricingPlan.planType,
      });
    }
  }, [isEditMode, selectedBundle?.id, selectedPricingPlan?.id, selectedPricingPlan?.groupId, selectedPricingPlan?.planType, accessToken, fetchPlanDetails, planDetails, fetchingPlanDetails]);

  // Populate form with fetched plan details
  useEffect(() => {
    const isMatchingPlan = planDetails && (
      planDetails.id === selectedPricingPlan?.id ||
      (planDetails.groupId && planDetails.groupId === selectedPricingPlan?.groupId)
    );
    const planIdentifier = planDetails?.groupId || planDetails?.id;

    if (isEditMode && isMatchingPlan && bundlePlan.planId !== planIdentifier) {
      setBundlePlanDetails({ field: 'PLAN_ID', value: planIdentifier });
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.PLAN_NAME, value: planDetails.planName || "" });
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.PLAN_VISIBILITY, value: planDetails.visibility || "private" });

      setIsDealerPortalChecked(planDetails.isDealerPlan || false);

      // Handle install fee fields - check if both installFee and installFeeCatalog exist
      const hasInstallFee = planDetails.installFee != null && planDetails.installFee !== "";
      const hasInstallFeeCatalog = planDetails.installFeeCatalog != null && planDetails.installFeeCatalog !== "";
      const shouldEnableInstallFee = hasInstallFee && hasInstallFeeCatalog;

      // Store original install fee state for comparison later
      setOriginalInstallFeeEnabled(shouldEnableInstallFee);
      setOriginalInstallFeeAmount(shouldEnableInstallFee ? (planDetails.installFee != null ? planDetails.installFee : "") : "");

      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.INSTALL_ENABLE, value: shouldEnableInstallFee });

      if (shouldEnableInstallFee) {
        setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.INSTALL_FEE, value: planDetails.installFee != null ? planDetails.installFee : "" });
        setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.INSTALL_FEE_CATALOG, value: planDetails.installFeeCatalog || "" });
      } else {
        setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.INSTALL_FEE, value: "" });
        setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.INSTALL_FEE_CATALOG, value: "" });
      }

      if (planDetails.planType === "advanced") {
        setBundlePlanDetails({
          field: ADVANCE_PLAN_CHANGE.PLAN_TYPE,
          value: { label: "Subscription with Overage", value: "advanced" },
        });
      } else {
        setBundlePlanDetails({
          field: ADVANCE_PLAN_CHANGE.PLAN_TYPE,
          value: { label: "Location Based", value: "subscription" },
        });
      }
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.PLAN_OVERAGE_CATALOG, value: planDetails.overageCatalog || "" });

      const price = planDetails.price || "";
      if (price) {
        const formattedPrice = price
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.MONTH_FEE, value: price });

        setOriginalPrice(price.toString());
      }
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.PLAN_CATALOG, value: planDetails.catalogNumber || "" });

      // Load sub-plans for advanced plans
      if (planDetails.planType === "advanced") {
        if (planDetails.subPlans && Array.isArray(planDetails.subPlans)) {
          loadSubPlansFromDetails(planDetails.subPlans);
        } else if (planDetails.tiers) {
          const tiersArr = Object.entries(planDetails.tiers).map(([key, tier]) => ({
            plan: key,
            priceId: tier.priceId, // Include priceId for update API calls
            name: tier.title,
            price: tier.basePrice,
            description: tier.description,
            containers: tier.containers,
            isActive: tier.isActive,
          }));
          loadSubPlansFromDetails(tiersArr);
        }
      }
    } else if (!isEditMode && bundlePlan.planId !== 'new') {
      setBundlePlanDetails({ field: 'PLAN_ID', value: 'new' });
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.PLAN_NAME, value: planName });
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.PLAN_TYPE, value: planType });
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.PLAN_VISIBILITY, value: planVisibility });

      setIsDealerPortalChecked(true);
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.MONTH_FEE, value: monthCost });
      setOriginalPrice("");
      setOriginalInstallFeeEnabled(false);
      setOriginalInstallFeeAmount("");
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.PLAN_CATALOG, value: catalogNumber });
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.INSTALL_ENABLE, value: isInstallFeeEnabled || false });
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.INSTALL_FEE, value: installFee || "" });
      setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.INSTALL_FEE_CATALOG, value: installFeeCatalogNumber || "" });
    }
  }, [planDetails, isEditMode, selectedPricingPlan?.id, bundlePlan.planId]);

  // Cleanup effect removed to preserve selected pricing plan when navigating to sub-screens

  // reducer functions for handling add/edit price plan actions
  const handlePlanNameChange = useCallback((event) => {
    setBundlePlanDetails({
      field: ADVANCE_PLAN_CHANGE.PLAN_NAME,
      value: event.target.value,
    });
  }, []);

  const handlePlanNameBlur = useCallback(
    (event) => {
      const planName = event.target.value.trim();
      let planNameError = "";

      if (planName === "") {
        planNameError = "Plan name is required.";
      } else if (
        isEditMode &&
        (planName === selectedPricingPlan?.planName ||
          planName === selectedPricingPlan?.groupName)
      ) {
        planNameError = "";
      } else {
        const planNameLower = planName.toLowerCase();
        const planNameExists = bundlePricingPlans
          .filter((plan) => plan.status === "active")
          .some((plan) => {
            let existingPlanName;
            if (plan?.groupName) {
              existingPlanName = plan?.groupName;
            } else {
              existingPlanName = plan?.planName;
            }
            return existingPlanName.toLowerCase() === planNameLower;
          });

        if (planNameExists) {
          planNameError = "Plan name already exists.";
        }
      }

      setIsPlanNameHasError(planNameError);
    },
    [isEditMode, selectedPricingPlan, bundlePricingPlans],
  );

  const handleMonthCostChange = useCallback((event) => {
    let raw = event.target.value.replace(/[^\d.]/g, "");
    const parts = raw.split(".");
    if (parts.length > 2) {
      raw = parts[0] + "." + parts.slice(1).join("");
    }
    let formatted = raw;
    if (raw.includes(".")) {
      const [integerPart, decimalPart] = raw.split(".");
      const limitedDecimalPart = decimalPart.slice(0, 2);
      const formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ",",
      );
      formatted = formattedInteger + "." + limitedDecimalPart;
    } else {
      formatted = raw.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    if (formatted.trim() === "") {
      setIsMonthCostValid(false);
    } else {
      setIsMonthCostValid(true);
    }
    setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.MONTH_FEE, value: formatted });
  }, []);

  const handleCatalogNumberChange = useCallback((event) => {
    const raw = event.target.value.replace(/\D/g, "");
    if (raw.trim() === "") {
      setIsCatalogNumberValid(false);
    } else {
      setIsCatalogNumberValid(true);
    }
    setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.PLAN_CATALOG, value: raw });
  }, []);

  const handleInstallFeeChange = useCallback((event) => {
    let raw = event.target.value.replace(/[^\d.]/g, "");
    const parts = raw.split(".");
    if (parts.length > 2) {
      raw = parts[0] + "." + parts.slice(1).join("");
    }
    let formatted = raw;
    if (raw.includes(".")) {
      const [integerPart, decimalPart] = raw.split(".");
      const limitedDecimalPart = decimalPart.slice(0, 2);
      const formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ",",
      );
      formatted = formattedInteger + "." + limitedDecimalPart;
    } else {
      formatted = raw.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    if (formatted.trim() === "") {
      setIsInstallFeeValid(false);
    } else {
      setIsInstallFeeValid(true);
    }
    setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.INSTALL_FEE, value: formatted });
  }, []);

  const handleInstallFeeCatalogNumberChange = useCallback((event) => {
    const raw = event.target.value.replace(/\D/g, "");
    if (raw.trim() === "") {
      setIsInstallFeeCatalogNumberValid(false);
    } else {
      setIsInstallFeeCatalogNumberValid(true);
    }
    setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.INSTALL_FEE_CATALOG, value: raw });
  }, []);

  const handleIsInstallFeeChange = useCallback((event) => {
    setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.INSTALL_ENABLE, value: event.target.checked });
  }, []);

  const handlePriceTypeSelection = useCallback((option) => {
    setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.PLAN_TYPE, value: option });
  }, []);

  const handleOverageCatalogNumberChange = useCallback((event) => {
    const raw = event.target.value.replace(/\D/g, "");
    if (raw.trim() === "") {
      setIsOverageCatalogNumberValid(false);
    } else {
      setIsOverageCatalogNumberValid(true);
    }
    setBundlePlanDetails({ field: ADVANCE_PLAN_CHANGE.PLAN_OVERAGE_CATALOG, value: raw });
  }, []);

  const breadcrumbs = useMemo(() => {
    return [
      {
        label: "Bundle Management",
        onClick: () => {
          setScreen(VIEW_BUNDLES);
          resetBundlePricingPlans();
          resetSelectedPricingPlan();
          resetBundlePlan();
        },
      },
      {
        label: "Bundle Pricing",
        onClick: () => {
          setScreen(PRICING_LIST_PLANS);
          resetSelectedPricingPlan();
          resetBundlePlan();
        },
      },
      { label: isEditMode ? "Edit a Plan" : "Add a Plan" },
    ];
  }, [setScreen, isEditMode, resetBundlePricingPlans, resetSelectedPricingPlan, resetBundlePlan]);

  const isSubPlanInvalid = (planToValidate) => {
    return (
      String(planToValidate?.title || "").trim() === "" ||
      planToValidate?.baseValue === "" ||
      String(planToValidate?.description || "").trim() === "" ||
      !planToValidate?.containers || planToValidate.containers.length === 0
    );
  };

  const handlePricingSave = () => {
    const pricePlanData = {
      planName: planName,
      visibility: planVisibility,
      isDealerPlan: isDealerPortalChecked,
    };

    // Using transformContainerForAPI from utils

    // Include sub-plans for advanced (Subscription with Overage) plans
    if (planType?.value === "advanced") {
      pricePlanData.planType = "advanced";
      pricePlanData.planCatalogNumber = parseInt(catalogNumber, 10);
      pricePlanData.overageCatalogNumber = parseInt(overageCatalogNumber, 10);
      pricePlanData.isInstallFeeEnabled = isInstallFeeEnabled;

      if (isInstallFeeEnabled) {
        pricePlanData.installFee = parseFloat(installFee.replace(/,/g, ""));
        pricePlanData.installFeeCatalogNumber = parseInt(installFeeCatalogNumber, 10);
      }

      // Include sub-plans based on visibility
      const tiers = {};
      if (planVisibility === PLAN_VISIBILITY.PUBLIC) {
        if (!isSubPlanInvalid(basic)) {
          tiers.basic = transformTierForAPI(basic, "Basic Tier");
        }
        if (!isSubPlanInvalid(plus)) {
          tiers.plus = transformTierForAPI(plus, "Plus Tier");
        }
        if (!isSubPlanInvalid(pro)) {
          tiers.pro = transformTierForAPI(pro, "Pro Tier");
        }
      } else {
        tiers.custom = transformTierForAPI({ ...custom, isActive: true }, "Custom Tier");
      }
      pricePlanData.tiers = tiers;
    } else {
      pricePlanData.planType = "subscription";
      pricePlanData.price = monthCost ? monthCost.replace(/,/g, "") : "";
      pricePlanData.catalogNumber = catalogNumber;

      if (isInstallFeeEnabled) {
        pricePlanData.installFee = installFee.replace(/,/g, "")
        pricePlanData.installFeeCatalog = installFeeCatalogNumber
      }
    }

    if (isEditMode) {
      updateBundlePricePlan({
        bundleId: selectedBundle.id,
        pricePlanId: selectedPricingPlan.id,
        groupId: selectedPricingPlan.groupId,
        planType: planType?.value,
        pricePlanData,
        accessToken
      });
    } else {
      createBundlePricePlan({ bundleId: selectedBundle.id, pricePlanData, accessToken });
    }
    // clear bundlePlanDetails in reducer to reset form on next add
  };

  const getValidationErrorMessage = () => {
    if (String(planName || "").trim() === "") {
      return "Enter Plan Name";
    }

    if (isPlanNameHasError != "") {
      return isPlanNameHasError;
    }

    if (isInstallFeeEnabled) {
      const feeValue = parseFloat(String(installFee || "").replace(/,/g, ""));
      const catalogValue = parseInt(installFeeCatalogNumber, 10);
      if (
        String(installFee || "").trim() === "" ||
        String(installFeeCatalogNumber || "").trim() === "" ||
        isNaN(feeValue) || feeValue < 0 ||
        isNaN(catalogValue) || catalogValue <= 0
      ) {
        return "Enter valid Install Fee details";
      }
    }

    if (config.isAdvancedPricePlanEnabled && planType && planType.value === "") {
      return "Select Plan Type";
    }

    const catalogValue = parseInt(catalogNumber, 10);

    if (!config.isAdvancedPricePlanEnabled || planType?.value === "subscription") {
      const priceValue = parseFloat(String(monthCost || "").replace(/,/g, ""));
      if (
        String(monthCost || "").trim() === "" ||
        String(catalogNumber || "").trim() === "" ||
        isNaN(priceValue) || priceValue < 0 ||
        isNaN(catalogValue) || catalogValue <= 0
      ) {
        return "Enter Monthly Cost and Plan Catalog Number";
      }
    } else if (planType?.value === "advanced") {
      const overageCatalogValue = parseInt(overageCatalogNumber, 10);
      if (
        String(catalogNumber || "").trim() === "" ||
        String(overageCatalogNumber || "").trim() === "" ||
        isNaN(catalogValue) || catalogValue <= 0 ||
        isNaN(overageCatalogValue) || overageCatalogValue <= 0
      ) {
        return "Enter valid Catalog Numbers";
      }

      if (planVisibility === PLAN_VISIBILITY.PUBLIC) {
        if (!basic?.isActive && !plus?.isActive && !pro?.isActive) {
          return "Enable at least one plan";
        }
        if (basic?.isActive && isSubPlanInvalid(basic)) return "Enter all details for Basic plan";
        if (plus?.isActive && isSubPlanInvalid(plus)) return "Enter all details for Plus plan";
        if (pro?.isActive && isSubPlanInvalid(pro)) return "Enter all details for Pro plan";
      } else {
        if (isSubPlanInvalid(custom)) {
          return "Enter all details for Custom plan";
        }
      }
    }

    return "";
  };

  const validationErrorMessage = getValidationErrorMessage();
  const isSaveDisabled = !!validationErrorMessage || creatingOrEditingPlan;

  const SaveButtonRenderer = (props) => (
    <span {...props} style={{ display: "inline-block", cursor: isSaveDisabled ? "not-allowed" : "pointer" }}>
      <PrimaryButton
        dataTestId="build_with_bundle"
        iconColor="primary"
        text="Save"
        onClick={async () => {
          const retirementStatus = determineRetirementType({
            planDetails,
            isInstallFeeEnabled,
            installFee,
            planType,
            config,
            monthCost,
            originalPrice,
            planVisibility,
            PLAN_VISIBILITY,
            tiers: { basic, plus, pro, custom }
          });

          if (isEditMode && retirementStatus !== RETIREMENT_TYPE.NONE) {
            setRetirementType(retirementStatus);
            setShowUpdateConfirmDialog(true);
            return;
          }

          // INFO: Check if adding a new public plan when one already exists
          if (
            !isEditMode &&
            planVisibility === PLAN_VISIBILITY.PUBLIC &&
            bundlePricingPlans &&
            bundlePricingPlans.some((plan) => {
              return (
                plan.visibility === PLAN_VISIBILITY.PUBLIC &&
                plan.status != PLAN_STATUS.RETIRED
              );
            })
          ) {
            setShowPublicPlanDialog(true);
            return;
          }

          await handlePricingSave();
        }}
        isDisabled={isSaveDisabled}
        style={{ marginRight: "16px" }}
      />
    </span>
  );

  const SaveButtonWithTooltip = validationErrorMessage && isSaveDisabled
    ? withTooltip(SaveButtonRenderer, { text: validationErrorMessage, placement: "top" })
    : SaveButtonRenderer;

  return (
    <>
      {errorFetchingPlanDetails && errorFetchingPlanDetailsMessage ? (
        <Dialog
          className="error-details-dialog"
          buttonsProps={[
            {
              id: "error-ok-action",
              onClick: () => {
                clearFetchPlanDetailsError();
                setScreen(PRICING_LIST_PLANS);
              },
              text: "OK",
              variant: BUTTON_VARIANTS.PRIMARY,
            },
          ]}
          id="error-details-dialog"
          isOpen={errorFetchingPlanDetails}
          onClose={() => {
            clearFetchPlanDetailsError();
            setScreen(PRICING_LIST_PLANS);
          }}
          title="Failed to Load Plan Details"
        >
          <div className="error-details">
            <div className="error-message">
              {errorFetchingPlanDetailsMessage}
            </div>
          </div>
        </Dialog>
      ) : null}
      {showAddEditPriceErrorDialog && addOrEditPricePlanError ? (
        <Dialog
          className="error-details-dialog"
          buttonsProps={[
            {
              id: "error-ok-action",
              onClick: () => {
                clearAddEditPricePlanError();
              },
              text: "Cancel",
              variant: BUTTON_VARIANTS.SECONDARY,
            },
          ]}
          id="error-details-dialog"
          isOpen={showAddEditPriceErrorDialog}
          onClose={() => {
            clearAddEditPricePlanError();
          }}
          title="Bundle Pricing Plan Failed"
        >
          <div className="error-details">
            <div className="error-message">{addOrEditPricePlanError}</div>
          </div>
        </Dialog>
      ) : null}
      {fetchingPlanDetails || creatingOrEditingPlan ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <Loader
            className="pricing-plans-loader"
            data-testid="pricing_plans_loader"
            label={
              fetchingPlanDetails ? "Loading plan details..." : "Saving..."
            }
            variant="inline"
          />
        </div>
      ) : (
        <Card>
          <div className="create-bundle-card">
            <div className="bundle-pricing-title">
              Pricing - {selectedBundle?.name}
            </div>

            <PricingBreadcrumbs breadcrumbs={breadcrumbs} />

            <div>
              <div className="bundle-pricing-text">
                {isEditMode ? "Edit a Plan" : "Add a Plan"}
              </div>
              <div />
              <div className="bundle_pricing_description">
                Bundle pricing is a flat monthly fee per location for unlimited
                API usage. Public plans are visible to all, while private plans
                are shared with specific organizations. ISV selections appear in
                the API Directory, and dealer selections are shown in the Dealer
                Portal for API buyers.
              </div>
            </div>
            <div>
              <div className="bundle_pricing_heading">
                <label htmlFor="overview-input">Overview</label>
              </div>
              <div className="input_box">
                <Input
                  id="input-simple"
                  label="Plan Name"
                  name="input-simple"
                  dataTestId="input-bundle-plan-name-field"
                  size={INPUT_SIZES.LARGE}
                  maxLength={100}
                  isRequired
                  enableCustomValidation
                  hasError={isPlanNameHasError !== ""}
                  errorMessage={isPlanNameHasError}
                  onChange={handlePlanNameChange}
                  onBlur={handlePlanNameBlur}
                  value={planName}
                />
              </div>
              <div className="input_box">
                <div className="bundle_pricing_sub_heading">
                  <label htmlFor="overview-input">Visibility</label>
                </div>
                <div style={{ marginLeft: "-14px" }}>
                  <Radio
                    className="fdn-pricing__radio"
                    checked={planVisibility === "private"}
                    label="Private"
                    value="private"
                    name="plan-visibility"
                    id="private-plan-visibility"
                    onChange={() => {
                      setBundlePlanDetails({
                        field: ADVANCE_PLAN_CHANGE.PLAN_VISIBILITY,
                        value: "private",
                      });
                    }}
                    labelPosition={RADIO_LABEL_POSITIONS.AFTER}
                    size={RADIO_SIZES.LARGE}
                    isDisabled={isEditMode}
                  />
                  <Radio
                    className="fdn-pricing__radio"
                    checked={planVisibility === "public"}
                    label="Public"
                    value="public"
                    name="plan-visibility"
                    id="public-plan-visibility"
                    onChange={() => {
                      setBundlePlanDetails({
                        field: ADVANCE_PLAN_CHANGE.PLAN_VISIBILITY,
                        value: "public",
                      });
                    }}
                    labelPosition={RADIO_LABEL_POSITIONS.AFTER}
                    size={RADIO_SIZES.LARGE}
                    isDisabled={isEditMode}
                  />
                </div>
              </div>
              <div className="input_box">
                <div className="bundle_pricing_sub_heading">
                  <label htmlFor="install-fee-input">Install Fee</label>
                </div>
                <Checkbox
                  style={{ marginLeft: "-14px" }}
                  checked={isInstallFeeEnabled}
                  label="Enable Install Fee"
                  name="install-fee-checkbox"
                  id="install-fee-checkbox"
                  onChange={handleIsInstallFeeChange}
                />
              </div>
              {isInstallFeeEnabled && (
                <>
                  <div className="input_box">
                    <Input
                      id="install-fee-input"
                      label="Install Fee Amount"
                      name="install-fee-input"
                      size={INPUT_SIZES.LARGE}
                      enableCustomValidation
                      hasError={!isInstallFeeValid}
                      errorMessage="Install fee amount is required."
                      prefixText="$"
                      isRequired
                      onChange={handleInstallFeeChange}
                      value={installFee}
                    />
                  </div>
                  <div className="input_box">
                    <Input
                      id="install-fee-catalog-number-input"
                      label="Install Fee Catalog Number"
                      name="install-fee-catalog-number-input"
                      helperText="Max 7 characters"
                      size={INPUT_SIZES.LARGE}
                      isRequired
                      enableCustomValidation
                      hasError={!isInstallFeeCatalogNumberValid}
                      errorMessage="Install fee catalog number is required."
                      onChange={handleInstallFeeCatalogNumberChange}
                      value={installFeeCatalogNumber}
                      maxLength={7}
                    />
                  </div>
                </>
              )}
              {!config.isAdvancedPricePlanEnabled && (
                <div className="input_box">
                  <Input
                    id="monthly-cost-input"
                    label="Monthly Cost Per Location"
                    name="monthly-cost-input"
                    size={INPUT_SIZES.LARGE}
                    enableCustomValidation
                    hasError={!isMonthCostValid}
                    errorMessage="Monthly cost is required."
                    prefixText="$"
                    isRequired
                    onChange={handleMonthCostChange}
                    value={monthCost}
                  />
                </div>
              )}
              {config.isAdvancedPricePlanEnabled && (
                <>
                  <div className="input_box">
                    <DropdownMenu
                      label="Plan Type"
                      dataTestId="plan-type-dropdown"
                      isRequired
                      onChange={handlePriceTypeSelection}
                      options={BUNDLE_PLAN_OPTIONS}
                      value={planType}
                      isDisabled={isEditMode}
                    />
                  </div>
                  {planType.value === "subscription" && (
                    <div className="input_box">
                      <Input
                        id="monthly-cost-input"
                        label="Monthly Cost Per Location"
                        name="monthly-cost-input"
                        size={INPUT_SIZES.LARGE}
                        prefixText="$"
                        isRequired
                        onChange={handleMonthCostChange}
                        value={monthCost}
                        enableCustomValidation
                        hasError={!isMonthCostValid}
                        errorMessage="Monthly cost is required."
                      />
                    </div>
                  )}
                </>
              )}

              <div className="input_box">
                <Input
                  id="catalog-number-input"
                  label="Plan Catalog Number"
                  name="catalog-number-input"
                  helperText="Max 7 characters"
                  size={INPUT_SIZES.LARGE}
                  isRequired
                  onChange={handleCatalogNumberChange}
                  value={catalogNumber}
                  maxLength={7}
                  enableCustomValidation
                  hasError={!isCatalogNumberValid}
                  errorMessage="Plan catalog number is required."
                />
              </div>

              {config.isAdvancedPricePlanEnabled &&
                planType.value === "advanced" && (
                  <>
                    <div className="input_box">
                      <Input
                        id="overage-catalog-number-input"
                        label="Overage Catalog Number"
                        name="overage-catalog-number-input"
                        helperText="Max 7 characters"
                        size={INPUT_SIZES.LARGE}
                        isRequired
                        onChange={handleOverageCatalogNumberChange}
                        value={overageCatalogNumber}
                        maxLength={7}
                        enableCustomValidation
                        hasError={!isOverageCatalogNumberValid}
                        errorMessage="Overage catalog number is required."
                      />
                    </div>
                    <div className="bundle_pricing_heading">
                      {planVisibility === PLAN_VISIBILITY.PUBLIC ? "Plans" : "Custom Plan"}
                    </div>
                    <div className={planVisibility === PLAN_VISIBILITY.PUBLIC ? "bundle_pricing_sub_heading" : "bundle_pricing_sub_heading-content"}>
                      {planVisibility === PLAN_VISIBILITY.PUBLIC
                        ? "Add up to 3 total plans - Basic, Plus and Pro"
                        : "With private APIs, you can create Custom pricing plans to meet the needs of select customers. Explicitly share your Custom plans with specific API subscribers and create as many individual plans as needed. Dealer Pricing plans are available to only private apps."}
                    </div>
                    <div className="overage-cards-container">
                      {bundlePlan.visibility === PLAN_VISIBILITY.PUBLIC ? (
                        <>
                          <AdvancePlanCard
                            {...props}
                            subPlan="basic"
                            plan={bundlePlan.basic}
                            installFee={bundlePlan.installFee}
                            isInstallFeeEnabled={isInstallFeeEnabled}
                          />
                          <AdvancePlanCard
                            {...props}
                            subPlan="plus"
                            plan={bundlePlan.plus}
                            installFee={bundlePlan.installFee}
                            isInstallFeeEnabled={isInstallFeeEnabled}
                          />
                          <AdvancePlanCard
                            {...props}
                            subPlan="pro"
                            plan={bundlePlan.pro}
                            installFee={bundlePlan.installFee}
                            isInstallFeeEnabled={isInstallFeeEnabled}
                          />
                        </>
                      ) : (
                        <AdvancePlanCard
                          {...props}
                          subPlan="custom"
                          plan={bundlePlan.custom}
                          installFee={bundlePlan.installFee}
                          isInstallFeeEnabled={isInstallFeeEnabled}
                        />
                      )}
                    </div>
                  </>
                )}
              <div
                className="bundle-pricing-buttons bundle_card-save-button"
              >
                <SaveButtonWithTooltip />
                <SecondaryButton
                  dataTestId="build_with_bundle"
                  iconColor="primary"
                  text="Cancel"
                  onClick={() => {
                    resetSelectedPricingPlan();
                    resetBundlePlan();
                    setScreen(PRICING_LIST_PLANS);
                  }}
                />
              </div>
              <Dialog
                isOpen={showPublicPlanDialog}
                onClose={() => setShowPublicPlanDialog(false)}
                title="Save Pricing Plan"
              >
                <div style={{ marginBottom: "16px" }}>
                  Saving this pricing plan will retire your existing public
                  pricing plan. Do you want to proceed?
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                  }}
                >
                  <PrimaryButton
                    text="OK"
                    onClick={async () => {
                      setShowPublicPlanDialog(false);
                      await handlePricingSave();
                    }}
                  />
                  <SecondaryButton
                    text="Cancel"
                    onClick={() => setShowPublicPlanDialog(false)}
                  />
                </div>
              </Dialog>

              <Dialog
                isOpen={showUpdateConfirmDialog}
                onClose={() => setShowUpdateConfirmDialog(false)}
                title="Update Pricing Plan"
              >
                <div style={{ marginBottom: "16px" }}>
                  {retirementType === RETIREMENT_TYPE.WHOLE
                    ? RETIREMENT_MESSAGES.WHOLE
                    : RETIREMENT_MESSAGES.TIER}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                  }}
                >
                  <SecondaryButton
                    text="OK"
                    onClick={async () => {
                      setShowUpdateConfirmDialog(false);
                      await handlePricingSave();
                    }}
                  />
                  <PrimaryButton
                    text="CANCEL"
                    onClick={() => setShowUpdateConfirmDialog(false)}
                  />
                </div>
              </Dialog>
            </div>
          </div>
        </Card>
      )}
    </>
  );
}

export default withNavigationToBundles(PricingAddPlan);
